import React, { useEffect, useState, useCallback } from 'react';
import { Button, Space, Tag } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined } from '@ant-design/icons';
import { timeEntriesApi } from '../../services/api/timeEntriesApi';

export interface TechnicianTimeTrackerProps {
  workOrderId: number;
  lineItemId: number;
  technicianId: number;
}

const TechnicianTimeTracker: React.FC<TechnicianTimeTrackerProps> = ({
  workOrderId,
  lineItemId,
  technicianId,
}) => {
  const [entries, setEntries] = useState<any[]>([]);
  const [running, setRunning] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await timeEntriesApi.list(workOrderId, lineItemId);
      setEntries(data);
      const open = data.find(
        (e: any) => !e.ended_at && e.technician_id === technicianId
      );
      setRunning(open ?? null);
    } catch {
      // A failed time-entries fetch must not surface as an unhandled promise
      // rejection (the effect below floats this promise). Degrade to an empty
      // list; the tracker simply shows no entries until the next successful load.
      setEntries([]);
      setRunning(null);
    }
  }, [workOrderId, lineItemId, technicianId]);

  useEffect(() => {
    load();
  }, [load]);

  const totalHours = entries.reduce((sum, e) => {
    if (!e.ended_at) return sum;
    const ms =
      new Date(e.ended_at).getTime() - new Date(e.started_at).getTime();
    return sum + ms / 3_600_000;
  }, 0);

  const toggle = async () => {
    setBusy(true);
    try {
      if (running) {
        await timeEntriesApi.stop(workOrderId, lineItemId, technicianId);
      } else {
        await timeEntriesApi.start(workOrderId, lineItemId, technicianId);
      }
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Space>
      <Button
        type={running ? 'default' : 'primary'}
        icon={running ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
        loading={busy}
        onClick={toggle}
        size="small"
      >
        {running ? 'Stop' : 'Start'}
      </Button>
      <Tag>{totalHours.toFixed(2)}h</Tag>
    </Space>
  );
};

export default TechnicianTimeTracker;
